import React from "react";
import {
  PerceptionTemplate,
  findPerceptionTemplates,
} from "../../services/perceptions.service.ts";

interface TemplateAutocompleteProps {
  searchValue: string;
  onTemplateSelect: (template: PerceptionTemplate) => void;
}

const TemplateAutocomplete: React.FC<TemplateAutocompleteProps> = ({
  searchValue,
  onTemplateSelect,
}) => {
  const [templates, setTemplates] = React.useState<PerceptionTemplate[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const formatMoney = (amount: number) => {
    return amount.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 2,
    });
  };

  const searchTemplates = async (query: string) => {
    setLoading(true);
    try {
      const limit = query && query.trim().length > 0 ? 10 : 4;
      const results = await findPerceptionTemplates(query, limit);
      setTemplates(results);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching templates:", error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!searchValue || searchValue.length === 0) {
      searchTemplates("");
      return;
    }

    timeoutRef.current = setTimeout(() => {
      searchTemplates(searchValue);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchValue]);

  if (loading) {
    return (
      <div className="mt-2">
        <div className="d-flex align-items-center justify-content-center py-2">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <span className="text-muted small">Buscando plantillas...</span>
        </div>
      </div>
    );
  }

  if (!hasSearched && !loading) {
    return null;
  }

  if (templates.length === 0 && hasSearched && !loading) {
    return null;
  }

  const isSearching = searchValue && searchValue.length > 0;
  const displayText = isSearching
    ? `Plantillas encontradas (${templates.length}):`
    : `Plantillas disponibles (${templates.length}):`;

  return (
    <div className="mb-4">
      <div className="d-flex flex-wrap gap-2" style={{ marginLeft: "193px" }}>
        {templates.map((template) => (
          <div
            key={template.id}
            className="badge bg-light text-dark border border-secondary/10 d-flex align-items-center"
            style={{
              cursor: "pointer",
              fontSize: "0.75rem",
              padding: "0.5rem 0.75rem",
              borderRadius: "0.375rem",
              transition: "all 0.2s ease",
              maxWidth: "200px",
            }}
            onClick={() => onTemplateSelect(template)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#007bff";
              e.currentTarget.style.color = "white";
              e.currentTarget.style.borderColor = "#007bff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
              e.currentTarget.style.color = "#212529";
              e.currentTarget.style.borderColor = "#6c757d";
            }}
            title={`Hacer clic para aplicar: ${template.name}`}
          >
            <div className="d-flex flex-column align-items-start">
              <div
                className="fw-medium text-truncate"
                style={{ maxWidth: "180px" }}
              >
                {template.name}
              </div>
              <div className="text-success small fw-bold">
                {formatMoney(template.amount)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateAutocomplete;
